import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { checkSession } from "./lib/api";
import { ToastProvider } from "./components/Toast";
import LoginPage from "./pages/LoginPage";
import ClientListPage from "./pages/ClientListPage";
import ClientWorkspacePage, {
  ClientWorkspaceIndexRedirect,
  ComingSoonTab,
  TABS,
} from "./pages/ClientWorkspacePage";
import ClientSettingsTab from "./pages/ClientSettingsTab";
import ContentGenerationTab from "./pages/ContentGenerationTab";
import CrawlTab from "./pages/CrawlTab";
import ContentAuditTab from "./pages/ContentAuditTab";
import GbpCategoriesTab from "./pages/GbpCategoriesTab";
import GeoPlannerTab from "./pages/GeoPlannerTab";
import ResearchTab from "./pages/ResearchTab";
import SuppPlannerTab from "./pages/SuppPlannerTab";
import SitemapsTab from "./pages/SitemapsTab";
import SettingsPage from "./pages/SettingsPage";

// Maps each tab's `component` key to the element it renders. Routing paths come
// from the TABS array (single source of truth in ClientWorkspacePage), so
// renaming a tab's path there updates the URL here automatically.
const TAB_ELEMENTS: Record<string, React.ReactNode> = {
  settings: <ClientSettingsTab />,
  gbp: <GbpCategoriesTab />,
  crawl: <CrawlTab />,
  audit: <ContentAuditTab />,
  gap: <ResearchTab />,
  topical: <SuppPlannerTab />,
  geo: <GeoPlannerTab />,
  content: <ContentGenerationTab />,
  internal: <ComingSoonTab label="Internal Hubs" />,
  sitemaps: <SitemapsTab />,
};

export default function App() {
  return (
    <ToastProvider>
      <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <ClientListPage />
          </RequireAuth>
        }
      />

      <Route
        path="/settings"
        element={
          <RequireAuth>
            <SettingsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/clients/:clientId"
        element={
          <RequireAuth>
            <ClientWorkspacePage />
          </RequireAuth>
        }
      >
        <Route index element={<ClientWorkspaceIndexRedirect />} />
        {TABS.map((tab) => (
          <Route
            key={tab.path}
            path={tab.path}
            element={TAB_ELEMENTS[tab.component]}
          />
        ))}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ToastProvider>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"checking" | "authed" | "unauthed">("checking");
  const location = useLocation();

  useEffect(() => {
    checkSession()
      .then((authed) => setState(authed ? "authed" : "unauthed"))
      .catch(() => setState("unauthed"));
  }, [location.pathname]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted text-sm">
        Loading...
      </div>
    );
  }

  if (state === "unauthed") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
